import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { useEffect, useState } from "react";
import { Button, Card, Table } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import api from "../../config/api";
import { getDate, LocalFormatDate, rupeeFormat } from "../../config/functions";
import {
  ClientRegistrationType,
  fetchClientRegistrationAction,
} from "../../redux/actions/clientRegistration";
import { AppDispatch, StoreState } from "../../redux/store";
import GstSalesReportFilter from "./GstSalesReportFilter";

interface InvoiceData {
  financialYear: string;
  date: string;
  invoiceNo: string;
  supplierName: string;
  supplierGSTIN: string;
  taxable: number;
  igst: number;
  sgst: number;
  cgst: number;
  gstValue: number;
  addAmount: number;
  lessAmount: number;
  roundOff: number | string;
  netTotal: number;
}

const GstPurchaseInvoiceWiseReport = () => {
  const dispatch = useDispatch<AppDispatch>();

  const [fromDate, setFromDate] = useState<string>("");
  const [toDate, setToDate] = useState<string>("");
  const [branchId, setBranchId] = useState<string>("");
  const [customerType, setCustomerType] = useState<string>("");
  const [tableData, setTableData] = useState<InvoiceData[]>([]);
  const [triggerSearch, setTriggerSearch] = useState<boolean>(false);

  const clients = useSelector<StoreState, ClientRegistrationType[]>(
    (state) => state.clientRegistration,
  );

  useEffect(() => {
    dispatch(fetchClientRegistrationAction());
  }, []);

  useEffect(() => {
    if (triggerSearch) {
      api()
        .get(
          `reports/gstPurchaseInvoiceWise/?fromDate=${getDate(fromDate)}&toDate=${getDate(
            toDate,
          )}&branchId=${branchId}&customerType=${customerType}`,
        )
        .then((res) => {
          if (res.status === 200 && res.data.data) {
            setTableData(res.data.data);
          }
        })
        .catch((error) => {
          console.error("Error fetching sales data:", error);
        });

      setTriggerSearch(false);
    }
  }, [triggerSearch, fromDate, toDate, branchId, customerType]);

  const [filters, setFilters] = useState<any>({});

  const handleSearch = (newFilters: any) => {
    setFilters(newFilters);
    setFromDate(newFilters.fromDate);
    setToDate(newFilters.toDate);
    setBranchId(newFilters.branchId);
    setCustomerType(newFilters.customerType);
    setTriggerSearch(true);
  };

  const uniqueGstinCount = new Set(
    (tableData || [])
      .map((r) => (r.supplierGSTIN || "").trim())
      .filter((gst) => gst.length > 0),
  ).size;

  const totalNetValue = (tableData || []).reduce(
    (sum, r) => sum + (Number(r.netTotal) || 0),
    0,
  );
  const totalTaxableValue = (tableData || []).reduce(
    (sum, r) => sum + (Number(r.taxable) || 0),
    0,
  );
  const totalIgst = (tableData || []).reduce((sum, r) => sum + (Number(r.igst) || 0), 0);
  const totalSgst = (tableData || []).reduce((sum, r) => sum + (Number(r.sgst) || 0), 0);
  const totalCgst = (tableData || []).reduce((sum, r) => sum + (Number(r.cgst) || 0), 0);
  const totalGstValue = (tableData || []).reduce((sum, r) => sum + (Number(r.gstValue) || 0), 0);
  const totalAddAmount = (tableData || []).reduce((sum, r) => sum + (Number(r.addAmount) || 0), 0);
  const totalLessAmount = (tableData || []).reduce((sum, r) => sum + (Number(r.lessAmount) || 0), 0);
  const totalRoundOff = (tableData || []).reduce((sum, r) => sum + (Number(r.roundOff) || 0), 0);

  const toRow = (item: InvoiceData, index: number) => [
    String(index + 1),
    item.financialYear ?? "",
    LocalFormatDate(item.date) ?? "",
    item.invoiceNo ?? "",
    item.supplierName ?? "",
    item.supplierGSTIN ?? "",
    rupeeFormat(item.taxable) ?? "0",
    rupeeFormat(item.igst) ?? "0",
    rupeeFormat(item.sgst) ?? "0",
    rupeeFormat(item.cgst) ?? "0",
    rupeeFormat(item.gstValue) ?? "0",
    rupeeFormat(item.addAmount) ?? "0",
    rupeeFormat(item.lessAmount) ?? "0",
    rupeeFormat(item.roundOff) ?? "0",
    rupeeFormat(item.netTotal) ?? "0",
  ];

  const downloadCSV = () => {
    const headerLabels = [
      "SI.No",
      "Financial Year",
      "Date",
      "Inv.No",
      "Supplier Name",
      "Supplier GSTIN",
      "Taxable Value",
      "IGST",
      "SGST",
      "CGST",
      "GST Value",
      "Add Amount",
      "Less Amount",
      "Round Off",
      "Net Total",
    ];

    const rows = tableData?.map((item, idx) => toRow(item, idx)) || [];

    rows.push([
      "", // SI.No
      "", // Financial Year
      "", // Date
      "", // Inv.No
      "", // Supplier Name
      `${uniqueGstinCount}`, // Supplier GSTIN count
      rupeeFormat(totalTaxableValue) ?? "0",
      rupeeFormat(totalIgst) ?? "0",
      rupeeFormat(totalSgst) ?? "0",
      rupeeFormat(totalCgst) ?? "0",
      rupeeFormat(totalGstValue) ?? "0",
      rupeeFormat(totalAddAmount) ?? "0",
      rupeeFormat(totalLessAmount) ?? "0",
      rupeeFormat(totalRoundOff) ?? "0",
      rupeeFormat(totalNetValue) ?? "0",
    ]);

    const csvCell = (value: unknown) => {
      const s = value === null || value === undefined ? "" : String(value);
      return `"${s.replace(/"/g, '""')}"`;
    };

    const csvContent = [
      headerLabels.map(csvCell).join(","),
      ...rows.map((row) => row.map(csvCell).join(",")),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `TAP_purchase_${customerType}_${LocalFormatDate(getDate(filters?.fromDate)) || ""}_${LocalFormatDate(getDate(filters?.toDate)) || ""}`;
    link.click();
  };

  const downloadPDF = () => {
    const logoUrl = clients[0]?.shopPhoto;

    const doc = new jsPDF("landscape");
    const marginX = 10;
    const boxWidth = doc.internal.pageSize.width - 2 * marginX;
    const boxHeight = 25;
    let startY = 15;

    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");

    doc.rect(marginX, startY - 5, boxWidth, boxHeight);

    if (logoUrl) {
      doc.addImage(logoUrl, "JPEG", marginX + 5, startY - 2, 25, 20);
    }

    const paddingBetweenHeaderAndTable = 3;
    const textX = marginX + 35;

    doc.text(clients[0]?.shopShortName || "", textX, startY);
    doc.setFont("helvetica", "normal");
    doc.text(
      `${clients[0]?.address1 || ""}, ${clients[0]?.address2 || ""}, ${
        clients[0]?.district || ""
      }`,
      textX,
      startY + 5,
    );
    doc.text(
      `${clients[0]?.state || ""} - ${clients[0]?.pincode || ""}, ${
        clients[0]?.contactNo1 || ""
      }`,
      textX,
      startY + 10,
    );
    doc.text(`GSTIN: ${clients[0]?.gstNo || ""}`, textX, startY + 15);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    const heading = `GST ${customerType} Purchase invoice wise report`;
    const headingWidth = doc.getTextWidth(heading);
    const pageWidth = doc.internal.pageSize.width;
    doc.text(heading, marginX + boxWidth - headingWidth - 10, startY + 8);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    const financialYearText = `Financial Year: ${
      tableData[0]?.financialYear || ""
    }`;
    const headingStartX = marginX + boxWidth - headingWidth - 10;
    doc.text(financialYearText, headingStartX, startY + 13);

    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    const fromDate =
      "From : " + (LocalFormatDate(getDate(filters?.fromDate)) || "");
    const toDate = "To : " + (LocalFormatDate(getDate(filters?.toDate)) || "");

    const tableStartY = startY + boxHeight + paddingBetweenHeaderAndTable;
    doc.text(fromDate, marginX, tableStartY);
    doc.text(toDate, marginX + 50, tableStartY);

    autoTable(doc, {
      startY: 47,
      showFoot: "lastPage",
      head: [
        [
          "SI.No",
          "Financial Year",
          "Date",
          "Inv.No",
          "Supplier Name",
          "Supplier GSTIN",
          "Taxable Value",
          "IGST",
          "SGST",
          "CGST",
          "GST Value",
          "Add Amount",
          "Less Amount",
          "Round Off",
          "Net Total",
        ],
      ],
      body: (tableData || []).map((item, idx) => toRow(item, idx)),
      foot: [
        [
          "",
          "",
          "",
          "",
          "",
          `${uniqueGstinCount}`,
          rupeeFormat(totalTaxableValue) ?? "0",
          rupeeFormat(totalIgst) ?? "0",
          rupeeFormat(totalSgst) ?? "0",
          rupeeFormat(totalCgst) ?? "0",
          rupeeFormat(totalGstValue) ?? "0",
          rupeeFormat(totalAddAmount) ?? "0",
          rupeeFormat(totalLessAmount) ?? "0",
          rupeeFormat(totalRoundOff) ?? "0",
          rupeeFormat(totalNetValue) ?? "0",
        ],
      ],
      theme: "grid",
      styles: { fontSize: 8, textColor: 0, lineColor: 0, lineWidth: 0.1 },
      headStyles: {
        fillColor: [255, 255, 255],
        textColor: 0,
        fontStyle: "bold",
        lineColor: 0,
        lineWidth: 0.1,
      },
      footStyles: {
        fillColor: [255, 255, 255],
        halign: "right",
      },
      margin: { top: 25, left: 10, right: 10 },
      columnStyles: {
        0: { halign: "center" },
        1: { halign: "left" },
        2: { halign: "center" },
        3: { halign: "left" },
        4: { halign: "left" },
        5: { halign: "left" },
        6: { halign: "right" },
        7: { halign: "right" },
        8: { halign: "right" },
        9: { halign: "right" },
        10: { halign: "right" },
        11: { halign: "right" },
        12: { halign: "right" },
        13: { halign: "right" },
        14: { halign: "right" },
      },
    });

    // Footer Section (Page Number)
    const pageCount = doc.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      let pageHeight = doc.internal.pageSize.height;
      let footerY = pageHeight - 11;

      doc.setFontSize(10);
      doc.text(`Page ${i} of ${pageCount}`, pageWidth - 30, footerY);
    }

    doc.save(
      `TAP_purchase_${customerType}_${LocalFormatDate(getDate(filters?.fromDate)) || ""}_${LocalFormatDate(getDate(filters?.toDate)) || ""}.pdf`,
    );
  };
  // tableData.map((item, index) => {
  //     console.log(item.quantity);
  // });

  return (
    <>
      <GstSalesReportFilter onSearch={handleSearch} showCustomerType={false} />
      {tableData.length > 0 ? (
        <Card className="mt-2 p-2 shadow-lg border-0 rounded-3">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h5 className="fw-bold">Purchase invoice details</h5>
            <div>
              <Button
                className="me-2 btn-sm"
                variant="success"
                onClick={downloadCSV}
              >
                Download CSV
              </Button>
              <Button
                className="me-2 btn-sm"
                variant="success"
                onClick={downloadPDF}
              >
                Download PDF
              </Button>
            </div>
          </div>
          <Table striped bordered hover>
            <thead>
              <tr>
                <th>SI.No</th>
                <th>Financial Year</th>
                <th>Date</th>
                <th>Inv.No</th>
                <th>Supplier Name</th>
                <th>Supplier GSTIN</th>
                <th>Taxable Value</th>
                <th>IGST</th>
                <th>SGST</th>
                <th>CGST</th>
                <th>GST Value</th>
                <th>Add Amount</th>
                <th>Less Amount</th>
                <th>Round Off</th>
                <th>Net Total</th>
              </tr>
            </thead>

            <tbody>
              {tableData?.map((item, index) => (
                <tr key={index}>
                  <td>{index + 1}</td>
                  <td>{item.financialYear}</td>
                  <td>{LocalFormatDate(item.date)}</td>
                  <td>{item.invoiceNo}</td>
                  <td>{item.supplierName}</td>
                  <td>{item.supplierGSTIN}</td>
                  <td>{rupeeFormat(item.taxable)}</td>
                  <td>{rupeeFormat(item.igst)}</td>
                  <td>{rupeeFormat(item.sgst)}</td>
                  <td>{rupeeFormat(item.cgst)}</td>
                  <td>{rupeeFormat(item.gstValue)}</td>
                  <td>{rupeeFormat(item.addAmount)}</td>
                  <td>{rupeeFormat(item.lessAmount)}</td>
                  <td>{rupeeFormat(item.roundOff)}</td>
                  <td>{rupeeFormat(item.netTotal)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card>
      ) : (
        <h4 className="text-center fw-bold text-secondary">
          No such data found!
        </h4>
      )}
    </>
  );
};

export default GstPurchaseInvoiceWiseReport;
